/* eslint-disable no-console */

import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import useAsync from 'react-use/lib/useAsync';
import { Entity } from '@backstage/catalog-model';

const GroupResourcePickerFieldSchema = makeFieldSchema({
  output: z => z.string(),
  uiOptions: z =>
    z.object({
      group_name: z
        .string()
        .optional()
        .describe(
          'Entity ref of the group to list resources from, e.g. "group:default/devops". Falls back to formData.group_name if omitted.',
        ),
      lifecycle: z
        .union([z.string(), z.array(z.string())])
        .optional()
        .describe(
          'Only include resources whose spec.lifecycle matches. Accepts a single string or an array, e.g. ["production", "ete"].',
        ),
      type: z
        .union([z.string(), z.array(z.string())])
        .optional()
        .describe(
          'Only include resources whose spec.type matches. Accepts a single string or an array, e.g. ["openshift", "database"].',
        ),
      filter: z
        .union([
          z
            .array(z.string())
            .describe(
              'Mode 1 — exclude resources by name, e.g. ["rosa1-ct1", "rosa2-ct1"].',
            ),
          z.object({
            component: z
              .union([
                z
                  .string()
                  .describe(
                    'Mode 2 — entity ref of a component whose dependsOn resources are excluded, e.g. "component:default/my-app".',
                  ),
                z
                  .object({
                    fieldName: z
                      .string()
                      .describe(
                        'Mode 3 — name of another form field whose value is the component entity ref, e.g. "service_name".',
                      ),
                  })
                  .describe(
                    'Mode 3 — resolve the component ref from another form field.',
                  ),
              ])
              .describe(
                'Component whose dependsOn resources will be excluded.',
              ),
          }),
        ])
        .optional()
        .describe('Exclude resources from the list.'),
    }),
});

export const GroupResourcePickerSchema = GroupResourcePickerFieldSchema;

type GroupResourcePickerProps = typeof GroupResourcePickerFieldSchema.TProps;

type FilterOption = string[] | { component: string | { fieldName: string } };

export const GroupResourcePicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  formContext,
  uiSchema,
  idSchema,
}: GroupResourcePickerProps) => {
  const catalogApi = useApi(catalogApiRef);

  const groupName =
    formContext.formData?.group_name || uiSchema['ui:options']?.group_name;

  console.log('[GroupResourcePicker] Extracted groupName:', groupName);

  // Pre-resolve filter component ref outside useAsync so it can be a stable dep
  const filterOption = uiSchema['ui:options']?.filter as
    FilterOption | undefined;
  let filterComponentRef: string | undefined;
  if (filterOption && !Array.isArray(filterOption) && filterOption.component) {
    if (typeof filterOption.component === 'string') {
      // Mode 2: static component name
      filterComponentRef = filterOption.component;
    } else {
      // Mode 3: read component name from another form field
      filterComponentRef =
        formContext.formData?.[filterOption.component.fieldName];
    }
  }

  const { value: resources = [] } = useAsync(async () => {
    if (!groupName) {
      console.log(
        '[GroupResourcePicker] Missing groupName. Returning empty resources.',
      );
      return [];
    }

    // 1. Fetch group entity and collect owned resource refs via ownerOf relations
    let group: Entity | undefined;
    try {
      group = await catalogApi.getEntityByRef(String(groupName));
      console.log(
        '[GroupResourcePicker] Fetched group:',
        group?.metadata?.name,
      );
    } catch (e) {
      console.error('[GroupResourcePicker] Error fetching group:', e);
      return [];
    }

    if (!group) {
      console.log('[GroupResourcePicker] Group not found.');
      return [];
    }

    const resourceRefs = (group.relations ?? [])
      .filter(r => r.type === 'ownerOf' && r.targetRef.startsWith('resource:'))
      .map(r => r.targetRef);
    console.log('[GroupResourcePicker] Found resource refs:', resourceRefs);

    if (!resourceRefs.length) {
      console.log('[GroupResourcePicker] No resource references found.');
      return [];
    }

    const lifecycleRaw = uiSchema['ui:options']?.lifecycle as
      string | string[] | undefined;
    let lifecycleFilter: string[] | undefined;
    if (lifecycleRaw) {
      lifecycleFilter = Array.isArray(lifecycleRaw)
        ? lifecycleRaw
        : [lifecycleRaw];
    }

    const resourceTypeOption = uiSchema['ui:options']?.type as
      string | string[] | undefined;
    let resourceTypeFilter: string[] | undefined;
    if (resourceTypeOption) {
      resourceTypeFilter = Array.isArray(resourceTypeOption)
        ? resourceTypeOption
        : [resourceTypeOption];
    }

    // 2. Build exclusion set from filter option
    const excludeNames = new Set<string>();

    if (filterOption) {
      if (Array.isArray(filterOption)) {
        // Mode 1: array of resource names to exclude directly
        filterOption.forEach(name => excludeNames.add(name));
      } else if (filterComponentRef) {
        // Mode 2 & 3: exclude resources that the resolved component depends on
        try {
          const component = await catalogApi.getEntityByRef(
            String(filterComponentRef),
          );
          if (component) {
            const dependsOn = (component.spec?.dependsOn ??
              component.spec?.dependOn) as string[] | undefined;
            const componentResourceRefs: string[] = Array.isArray(dependsOn)
              ? dependsOn
              : (component.relations ?? [])
                  .filter(r => r.type === 'dependsOn')
                  .map(r => r.targetRef);
            componentResourceRefs.forEach(ref => {
              // Extract name from ref e.g. "resource:devops/rosa1-ct1" -> "rosa1-ct1"
              const name = ref.split('/').pop();
              if (name) excludeNames.add(name);
            });
          }
        } catch (e) {
          console.error(
            '[GroupResourcePicker] Error fetching filter component:',
            e,
          );
        }
      }
    }
    console.log('[GroupResourcePicker] Exclude names:', [...excludeNames]);

    // 3. Fetch resource entities and apply lifecycle, type, and filter
    const resourceEntities: Entity[] = [];
    for (const ref of resourceRefs) {
      try {
        const resource = await catalogApi.getEntityByRef(ref);
        if (resource) {
          if (
            lifecycleFilter &&
            !lifecycleFilter.includes(resource.spec?.lifecycle as string)
          ) {
            console.log(
              `[GroupResourcePicker] Resource ${ref} skipped (lifecycle mismatch).`,
            );
            continue;
          }
          if (
            resourceTypeFilter &&
            !resourceTypeFilter.includes(resource.spec?.type as string)
          ) {
            console.log(
              `[GroupResourcePicker] Resource ${ref} skipped (type mismatch).`,
            );
            continue;
          }
          if (excludeNames.has(resource.metadata.name)) {
            console.log(
              `[GroupResourcePicker] Resource ${ref} skipped (filtered out).`,
            );
            continue;
          }
          resourceEntities.push(resource);
          console.log(`[GroupResourcePicker] Resource ${ref} matched!`);
        } else {
          console.log(
            `[GroupResourcePicker] Resource ${ref} could not be resolved.`,
          );
        }
      } catch (err) {
        console.error(
          `[GroupResourcePicker] Error fetching resource ${ref}:`,
          err,
        );
      }
    }

    console.log(
      '[GroupResourcePicker] Final matched resource entities:',
      resourceEntities.length,
    );
    return resourceEntities;
  }, [catalogApi, groupName, uiSchema, filterComponentRef]);

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
      fullWidth
    >
      <InputLabel htmlFor={idSchema?.$id}>
        {uiSchema['ui:title'] || 'Resource'}
      </InputLabel>
      <Select
        id={idSchema?.$id}
        value={formData || ''}
        name={idSchema?.$id}
        onChange={e => onChange(e.target.value as string)}
        displayEmpty
      >
        {resources.map(resource => {
          const stringifiedRef = `${resource.kind}:${
            resource.metadata.namespace || 'default'
          }/${resource.metadata.name}`;
          return (
            <MenuItem key={stringifiedRef} value={stringifiedRef}>
              {resource.metadata.name}
            </MenuItem>
          );
        })}
      </Select>
      <FormHelperText>
        {uiSchema['ui:description'] || 'Select a resource.'}
      </FormHelperText>
    </FormControl>
  );
};

export const GroupResourcePickerExamples = [
  {
    description:
      'Static group with lifecycle and type filter — lists only ETE OpenShift resources owned by the devops group.',
    example: {
      'ui:field': 'GroupResourcePicker',
      'ui:options': {
        group_name: 'group:default/devops',
        lifecycle: 'ete',
        type: 'openshift',
      },
    },
  },
  {
    description:
      'Dynamic group name — reads group_name from a previous form field instead of hardcoding it.',
    example: {
      'ui:field': 'GroupResourcePicker',
      'ui:options': {
        lifecycle: 'ete',
      },
    },
  },
  {
    description:
      'Multiple resource types — lists ETE resources of type openshift or database.',
    example: {
      'ui:field': 'GroupResourcePicker',
      'ui:options': {
        group_name: 'group:default/devops',
        lifecycle: 'ete',
        type: ['openshift', 'database'],
      },
    },
  },
  {
    description:
      'Filter mode 1 — exclude specific resources by name from the list.',
    example: {
      'ui:field': 'GroupResourcePicker',
      'ui:options': {
        group_name: 'group:default/devops',
        lifecycle: 'ete',
        filter: ['rosa1-ct1', 'rosa2-ct1'],
      },
    },
  },
  {
    description:
      'Filter mode 2 — exclude resources already used by a specific component.',
    example: {
      'ui:field': 'GroupResourcePicker',
      'ui:options': {
        group_name: 'group:default/devops',
        lifecycle: 'ete',
        filter: {
          component: 'component:testingabc/my-app',
        },
      },
    },
  },
  {
    description:
      'Filter mode 3 — exclude resources already used by the component selected in another form field.',
    example: {
      'ui:field': 'GroupResourcePicker',
      'ui:options': {
        group_name: 'group:default/devops',
        lifecycle: 'ete',
        filter: {
          component: {
            fieldName: 'service_name',
          },
        },
      },
    },
  },
];
